import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body, Numeric, Utility } from '@swyftx/aviary/atoms/Typography';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem/ReviewItem';
import StepModal from '@swyftx/aviary/molecules/StepModal';
import { MarginOrderDirection, ProtectionOrderVariant } from '@swyftx/margin/build/types';
import { MarginPositionStatus } from '@swyftx/margin/build/types';

import { FiatIdEnum } from '@shared/enums';
import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions/useMarginPositionDetails';
import { MarginPositionDetailsStep } from 'src/lib/margin/types';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { MarginPositionDetailsRootStepActions } from './MarginPositionDetailsRootStepActions';
import {
  MarginInstrument,
  MarginPositionsProtectionOrder,
  MarginProfitLoss,
  TotalMarginPositionSize,
  TotalMarginRequired,
} from '../../../../General';

type Props = {
  positionId?: string;
  onClose: () => void;
  onNextStep: (step: number, pushToStack?: boolean | undefined) => void;
  onAddProtectionOrder: (orderType: ProtectionOrderVariant) => void;
  onRemoveProtectionOrder: (orderType: ProtectionOrderVariant) => void;
};

const MarginPositionDetailsRootStep: React.FC<Props> = observer(
  ({ onClose, onNextStep, onAddProtectionOrder, onRemoveProtectionOrder, positionId }) => {
    const { getAssetById } = useMarkets();

    const aud = useMemo(() => getAssetById(FiatIdEnum.AUD), [getAssetById]);
    const {
      position,
      positionMarket,
      usedMargin,
      type,
      commission,
      priceScale,
      positionMarketLotsPrecision,
      price,
      currencyPrice,
      currentPrice,
      isPending,
      isOpen,
      currentAudPrice,
    } = useMarginPositionDetails({ positionId });
    const { t } = useTranslation('margin');

    const sideText = useMemo(
      () =>
        position?.sell ? t(`tradeSide.${MarginOrderDirection.Short}`) : t(`tradeSide.${MarginOrderDirection.Long}`),
      [position?.sell, t],
    );

    const subTitle = useMemo(() => {
      if (!position) return undefined;

      let time;

      if (isPending && position.lastUserAction) {
        time = t('position.title.lastUpdated', {
          dateTime: DateTime.fromMillis(position.lastUserAction).toFormat('dd MMM yyyy hh:mm:ss a'),
        });
      } else if (position.status === MarginPositionStatus.Open && position.opened) {
        time = t('position.title.opened', {
          dateTime: DateTime.fromMillis(position.opened).toFormat('dd MMM yyyy hh:mm:ss a'),
        });
      } else if (position.status === MarginPositionStatus.Closed && position.closed) {
        time = t('position.title.closed', {
          dateTime: DateTime.fromMillis(position.closed).toFormat('dd MMM yyyy hh:mm:ss a'),
        });
      }

      return (
        <FlexLayout alignItems='center' spacing={4}>
          <Utility variant='caption'>{t('position.title.id', { id: position.positionIdCode })}</Utility>
          {time && <Utility variant='caption'>• {time}</Utility>}
        </FlexLayout>
      );
    }, [isPending, position, t]);

    return (
      <StepModal.Step
        key={MarginPositionDetailsStep.ManageProtectionOrders}
        onClose={onClose}
        position='dynamic'
        hideActions
        title={t(`tradeStatus.${position?.status}` as any)}
        subTitle={subTitle}
      >
        <FlexLayout
          direction='column'
          spacing={16}
          className='h-full px-16 pb-16 md:px-24 md:pb-24'
          justifyContent='space-between'
        >
          <FlexLayout direction='column' spacing={16}>
            <FlexLayout direction='column' spacing={12}>
              {isPending && position?.issued && (
                <ReviewItem title={t('position.date.title')}>
                  <Body color='primary' weight='bold'>
                    {DateTime.fromMillis(position.issued).toFormat('dd MMM yyyy hh:mm:ss a')}
                  </Body>
                </ReviewItem>
              )}
              <ReviewItem title={t('position.instrument.title')}>
                <MarginInstrument market={positionMarket} size='medium' weight='bold' />
              </ReviewItem>
              {!!type && isPending && (
                <ReviewItem title={t('position.type.title')}>
                  <Body color='primary' weight='bold'>
                    {t(`tradeType.${type}`)}
                  </Body>
                </ReviewItem>
              )}
              <ReviewItem title={t('position.side.title')}>
                <Body color='primary' weight='bold'>
                  {sideText}
                </Body>
              </ReviewItem>
              {position?.closed && !position.filledOpen && (
                <ReviewItem title={t('position.timeClosed.title')}>
                  <Body color='primary' weight='bold'>
                    {DateTime.fromMillis(position.closed).toFormat('dd MMM yyyy hh:mm:ss a')}
                  </Body>
                </ReviewItem>
              )}
              <ReviewItem title={t('position.sizeLots.title')}>
                <Numeric color='primary' weight='bold'>
                  {formatCurrency(
                    isPending
                      ? Big(position?.volume || '0')
                      : Big(position?.filledOpen || '0').minus(position?.filledClose || '0'),
                    undefined,
                    {
                      priceScale: positionMarketLotsPrecision,
                    },
                  )}
                </Numeric>
              </ReviewItem>
              {isPending && price && (
                <ReviewItem title={t('position.price.title')}>
                  <Numeric color='primary' weight='bold'>
                    {formatCurrency(price, undefined, { priceScale })}
                  </Numeric>
                </ReviewItem>
              )}
              {isPending && currentPrice && (
                <ReviewItem title='Current price'>
                  <Numeric color='primary' weight='bold'>
                    {formatCurrency(currentPrice, undefined, { priceScale })}
                  </Numeric>
                </ReviewItem>
              )}
              {isPending && (
                <ReviewItem
                  title={t('position.expiry.title')}
                  name={t('position.expiry.name')}
                  tooltip={t('position.expiry.tooltip')}
                  underline='dashed'
                >
                  <Body weight='bold'>{t('position.expiry.content')}</Body>
                </ReviewItem>
              )}
              {position?.volume && currentAudPrice && (
                <ReviewItem
                  title={t('position.totalPositionSize.title')}
                  name={t('position.totalPositionSize.name')}
                  tooltip={t('position.totalPositionSize.tooltip')}
                  underline='dashed'
                >
                  <TotalMarginPositionSize lots={position.volume} price={currentAudPrice.toString()} valueOnly />
                </ReviewItem>
              )}
              {isPending && position?.volume && (
                <ReviewItem
                  title={t('position.totalMarginRequired.title')}
                  name={t('position.totalMarginRequired.name')}
                  tooltip={t('position.totalMarginRequired.tooltip')}
                  underline='dashed'
                >
                  <TotalMarginRequired
                    valueOnly
                    title={t('position.totalMarginRequired.title')}
                    lots={position.volume}
                    price={currencyPrice.toString()}
                    contractSize={positionMarket?.contractSize}
                    maxLeverage={positionMarket?.maxLeverage}
                  />
                </ReviewItem>
              )}
              {!isPending && position?.openPrice && (
                <ReviewItem
                  underline='dashed'
                  title={t('position.averageFillPrice.title')}
                  tooltip={t('position.averageFillPrice.tooltip')}
                >
                  <Numeric color='primary' weight='bold'>
                    {formatCurrency(position?.openPrice, undefined, { priceScale })}
                  </Numeric>
                </ReviewItem>
              )}
              {!isPending && position?.currentPrice && (
                <ReviewItem title={t('position.currentPrice.title')}>
                  <Numeric color='primary' weight='bold'>
                    {formatCurrency(position?.currentPrice, undefined, { priceScale })}
                  </Numeric>
                </ReviewItem>
              )}
              {isOpen && (
                <ReviewItem
                  underline='dashed'
                  title={t('position.usedMargin.title')}
                  tooltip={t('position.usedMargin.tooltip')}
                >
                  <Numeric color='primary' weight='bold'>
                    {formatCurrency(usedMargin, aud, { appendCode: true, hideCode: false })}
                  </Numeric>
                </ReviewItem>
              )}
              {!isPending && (
                <ReviewItem
                  underline='dashed'
                  title={t('position.profitLoss.title')}
                  tooltip={t('position.profitLoss.tooltip')}
                >
                  <MarginProfitLoss size='medium' weight='bold' positionId={positionId} />
                </ReviewItem>
              )}
            </FlexLayout>

            {(isPending || isOpen) && (
              <>
                <Separator />

                <FlexLayout direction='column' spacing={8}>
                  <Utility variant='caption' weight='bold' color='primary'>
                    {t('position.protectionOrder.title')}
                  </Utility>
                  <ReviewItem title={t('position.protectionOrder.stopLoss')}>
                    <MarginPositionsProtectionOrder
                      value={position?.stopLoss}
                      position={position}
                      protectionOrderType={ProtectionOrderVariant.StopLoss}
                      size='large'
                      onClick={() => onAddProtectionOrder(ProtectionOrderVariant.StopLoss)}
                      onRemove={() => onRemoveProtectionOrder(ProtectionOrderVariant.StopLoss)}
                    />
                  </ReviewItem>
                  <ReviewItem title={t('position.protectionOrder.takeProfit')}>
                    <MarginPositionsProtectionOrder
                      value={position?.takeProfit}
                      position={position}
                      protectionOrderType={ProtectionOrderVariant.TakeProfit}
                      size='large'
                      onClick={() => onAddProtectionOrder(ProtectionOrderVariant.TakeProfit)}
                      onRemove={() => onRemoveProtectionOrder(ProtectionOrderVariant.TakeProfit)}
                    />
                  </ReviewItem>
                </FlexLayout>
              </>
            )}
            <Separator />

            <FlexLayout direction='column' spacing={8}>
              <Body size='xsmall' weight='emphasis' color='primary'>
                {t('position.fees.title')}
              </Body>
              <ReviewItem
                underline='dashed'
                title={
                  isPending ? t('position.estimatedCommission.title') : t('instruments.information.commission.title')
                }
                tooltip={
                  isPending
                    ? t('position.estimatedCommission.tooltip')
                    : t('instruments.information.commission.tooltip')
                }
                name={t('position.estimatedCommission.name')}
              >
                <Numeric color='primary' weight='bold'>
                  ≈ {formatCurrency(commission?.round(2, 0), aud, { appendCode: true, hideCode: false })}
                </Numeric>
              </ReviewItem>
              {isOpen && (
                <ReviewItem title={t('position.overnightFee.title')}>
                  <Numeric color='primary' weight='bold'>
                    {formatCurrency(Big(position?.swap || '0').round(2, 0), aud, { appendCode: true, hideCode: false })}
                  </Numeric>
                </ReviewItem>
              )}
            </FlexLayout>
          </FlexLayout>

          {position && (
            <MarginPositionDetailsRootStepActions
              positionId={position.positionId}
              onNextStep={onNextStep}
              onClose={onClose}
            />
          )}
        </FlexLayout>
      </StepModal.Step>
    );
  },
);

export { MarginPositionDetailsRootStep };
